import { CSSProperties, FC } from 'react';
import { PageCountdown } from '~/components/countdown/PageCountdown';
import { VisuallyHiddenH1 } from '~/components/typography/VisuallyHiddenH1';
import {
  getScratchDesign,
  SCRATCH_SHARED_ASSETS,
} from '~/common/constants/scratchDesigns';
import { ScratchCard } from './ScratchCard';
import { type ScratchDesignId } from '~/common/constants/scratchDrop';
import { Product } from '~/types/product';
import {
  ScratchFooterEight,
  ScratchFooterNote,
  ScratchInner,
  ScratchSection,
  ScratchShippingNote,
} from './styles';

export const SCRATCH_DROP_FOOTER_NOTE =
  'DROPS - NEW DROP EVERY HOUR, AVAILABLE FOR 1 HOUR ONLY';

export const SCRATCH_DROP_SHIPPING_NOTE =
  'FREE SHIPPING AFTER YOUR FIRST ORDER - UK, EU & US APPLIED AUTOMATICALLY AT THE CHECKOUT';

interface ScratchDropProps {
  products: Product[];
  designId: ScratchDesignId;
  countdownTitle: string | null;
  /** Epoch ms. Already rejected as unparseable or past by `parseCountdownAt`. */
  countdownAt: number | null;
}

/**
 * The Scratch Drop homepage takeover (BPES-237).
 *
 * NOT lazy-loaded, deliberately, and against the `shop.tsx` precedent. `React.lazy` under a
 * null fallback would keep the camo URL out of the first server-rendered HTML, which defeats
 * the whole reason the background lives in `app.css` — on the campaign's highest-traffic
 * hour, on its LCP element. `_index.tsx`'s chunk is only fetched for `/` and already carries
 * `HomeHero` and `HomeFlashSale`; a third sibling is consistent.
 *
 * The app shell is reused as-is: the left nav in the mockup is the existing `Sidebar` and
 * the header is the existing `Header`, so `app/layout/index.tsx` needs no changes — the
 * scratch drop renders through `ConditionalLayout`'s DEFAULT branch, unlike the flash sale
 * which drops the Sidebar.
 */
export const ScratchDrop: FC<ScratchDropProps> = ({
  products,
  designId,
  countdownTitle,
  countdownAt,
}) => {
  const design = getScratchDesign(designId);

  // Mirrors `PageCountdown`'s own render condition, so the header clearance below cannot
  // outlive the thing it exists for.
  const hasCountdown = Boolean(countdownTitle) || countdownAt !== null;

  // Inline custom properties, not a styled-component: see `.scratch-camo` in app.css.
  const themeVars = {
    '--scratch-camo': `url(${design.camo})`,
    '--scratch-footer-primary': design.palette.footerPrimary,
    '--scratch-footer-shipping': design.palette.footerShipping,
    '--scratch-badge': design.palette.badge,
    '--scratch-glow': design.palette.glow,
    '--scratch-card-surface': `url(${SCRATCH_SHARED_ASSETS.cardSurface})`,
    '--scratch-badge-mask': `url(${SCRATCH_SHARED_ASSETS.badgeLogoMask})`,
  } as CSSProperties;

  return (
    <ScratchSection
      className="scratch-camo"
      style={themeVars}
      $hasCountdown={hasCountdown}
    >
      {/* The page's only real headings are raster artwork, so without this the document
          has no readable heading at all. */}
      <VisuallyHiddenH1>Broken Planet Scratch Dropday</VisuallyHiddenH1>

      <PageCountdown title={countdownTitle} at={countdownAt} />

      <ScratchInner>
        <ScratchCard products={products} hero={design.hero} />

        <ScratchFooterNote>
          <ScratchFooterEight
            src={SCRATCH_SHARED_ASSETS.footerEight}
            alt=""
            width={28}
            height={20}
          />
          {SCRATCH_DROP_FOOTER_NOTE}
        </ScratchFooterNote>
        <ScratchShippingNote>{SCRATCH_DROP_SHIPPING_NOTE}</ScratchShippingNote>
      </ScratchInner>
    </ScratchSection>
  );
};

export default ScratchDrop;
